import { AuthError } from '@supabase/supabase-js';

const AUTH_ERROR_MESSAGES = {
  'Invalid login credentials': 'Invalid email or password',
  'invalid_credentials': 'Invalid email or password',
  'invalid_grant': 'Invalid email or password',
  'Email not confirmed': 'Please verify your email address',
  'Database error granting user': 'Service temporarily unavailable. Please try again in a moment.',
  'unexpected_failure': 'Service temporarily unavailable. Please try again in a moment.',
  'User not found': 'No account found with this email',
  'Rate limit exceeded': 'Too many attempts. Please try again later.',
  'Network error': 'Connection error. Please check your internet connection.',
  'Invalid user credentials': 'Invalid email or password',
  'Invalid password': 'Invalid email or password',
  'Failed to fetch': 'Connection error. Please check your internet connection.',
  'TypeError: Failed to fetch': 'Connection error. Please check your internet connection.',
  'default': 'An error occurred. Please try again.'
};

export function isAuthError(error: unknown): error is AuthError {
  return error instanceof Error && 
         (
           '__isAuthError' in error || 
           error.name === 'AuthApiError' ||
           error.name === 'AuthError'
         );
}

function isSessionError(error: AuthError): boolean {
  return error.message?.includes('session') || 
         error.message?.includes('Session') ||
         error.status === 403;
}

function isRefreshTokenError(error: AuthError): boolean {
  return error.message?.includes('refresh_token') ||
         error.code === 'refresh_token_not_found' ||
         error.status === 400;
}

export function getAuthErrorMessage(error: AuthError | Error | unknown): string {
  if (isAuthError(error)) {
    // Check for specific error messages
    if (error.status === 500 || error.message?.includes('Database error')) {
      return 'Service temporarily unavailable. Please try again in a moment.';
    }

    for (const [key, message] of Object.entries(AUTH_ERROR_MESSAGES)) {
      if (error.message?.includes(key) || error.code === key || error.name === key) {
        return message;
      }
    }
    
    // Handle status codes
    switch (error.status) {
      case 400:
        return error.message?.includes('credentials') 
          ? 'Invalid email or password'
          : 'Invalid request. Please try again.';
      case 500:
        return 'Service temporarily unavailable. Please try again.';
      case 401:
        return 'Invalid email or password';
      case 422:
        return 'Account already exists with this email';
      case 429:
        return 'Too many attempts. Please try again later';
      default:
        return AUTH_ERROR_MESSAGES[error.message as keyof typeof AUTH_ERROR_MESSAGES] 
          || error.message 
          || AUTH_ERROR_MESSAGES.default;
    }
  }

  if (error instanceof Error) {
    if (error.message.toLowerCase().includes('fetch') || error.message.toLowerCase().includes('network')) {
      return AUTH_ERROR_MESSAGES['Network error'];
    }
    return error.message;
  }

  return AUTH_ERROR_MESSAGES.default;
}